import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { appNotify } from 'store/actions';
import { getProspectsByTxnOppIds } from 'api/prospector/prospects';
import { parseISO, format } from 'date-fns';
import StatusChip from 'components/explore/prospector/layout/StatusChip';

const UnderwriteInfoModal = ({
  isOpen,
  classes,
  setIsOpen,
  appNotify,
  selectedRows
}) => {
  const [prospectInfo, setProspectInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      getProspectsByTxnOppIds([selectedRows[0].id]).then(res => {
        if (res) {
          setProspectInfo(res[0]);
          setIsLoading(false);
        }
      }).catch(error => {
        console.log(error);
        setIsLoading(false);
        appNotify(`Error fetching prospect info: ${error.message}`, 'warning');
      })
    }
  }, [isOpen]);

  const handleClose = () => {
    setProspectInfo(null);
    setIsOpen(false);
  }

  return (
    <>
      {
        prospectInfo ?
          <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle disableTypography={true} className={classes.dialogTitleRoot}>
              <Typography variant={'h6'}>
                Prospect Info - {prospectInfo?.streetAddress}
              </Typography>
            </DialogTitle>
            <IconButton className={classes.closeButton} onClick={handleClose}>
              <ClearIcon fontSize="inherit" />
            </IconButton>
            <DialogContent className={classes.dialogContentRoot}>
            {!isLoading 
              ? 
              <div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Opportunity ID</Typography>
                  <Typography variant='body2'>{selectedRows[0]?.id}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Prospect ID</Typography>
                  <Typography variant='body2'>{prospectInfo?.id}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Sourcing User</Typography>
                  <Typography variant='body2'>{prospectInfo?.createdBy}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Prospect Date</Typography>
                  <Typography variant='body2'>{format(parseISO(prospectInfo.createdOn), 'MM/dd/yyyy, h:mm:ss a')}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Prospect Status</Typography>
                  {prospectInfo?.status ? <StatusChip status={prospectInfo.status} /> : 'N/A' }
                </div>
                <Divider/>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Beds</Typography>
                  <Typography variant='body2'>{prospectInfo?.beds}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Baths</Typography>
                  <Typography variant='body2'>{prospectInfo?.baths}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Year Built</Typography>
                  <Typography variant='body2'>{prospectInfo?.yearBuilt}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Sq Ft</Typography>
                  <Typography variant='body2'>{prospectInfo.sqft && prospectInfo.sqft.toLocaleString('en')}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Asking Price</Typography>
                  <Typography variant='body2'>{prospectInfo.listingPrice && `$${prospectInfo.listingPrice.toLocaleString('en')}`}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Strategy</Typography>
                  <Typography variant='body2'>{prospectInfo?.strategy}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Inspection Period</Typography>
                  <Typography variant='body2'>{prospectInfo.inspectionPeriod ? 'Yes' : 'No'}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Opportunity Source</Typography>
                  <Typography variant='body2'>{prospectInfo.opportunitySource}</Typography>
                </div>
                <div className={classes.infoGroup}>
                  <Typography variant='body2' className={classes.label}>Notes</Typography>
                  <Typography variant='body2'>{prospectInfo?.notes}</Typography>
                </div>
              </div>
              : <div className={classes.loadingWrapper}>
                  <CircularProgress />
                </div>
            }
            </DialogContent>
          </Dialog>
          : null
      }
    </>
  );
};

const styles = theme => ({
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  filterField: {
    marginTop: 16
  },
  label: {
    marginRight: '1rem',
    whiteSpace: 'nowrap'
  },
  dialogTitleRoot: {
    paddingBottom: 0
  },
  dialogContentRoot: {
    marginBottom: '1rem',
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  overlay: {
    height: 'calc(100vh - (100vh - 100%) - 48px)',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto'
  },
  infoGroup: {
    display: 'flex',
    justifyContent: 'space-between'
  }
});

const mapState = state => {
  return {
    user: state.appState.user
  }
};

const mapDispatch = dispatch => {
  return {
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options)),
  }
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(UnderwriteInfoModal);